import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Modal } from "antd";
import backBtnMobile from "../../assets/images/backBtnMobile.svg";
import backBtn from "../../assets/images/backBtn.svg";
import EducationalContentHeader from "./EducationalContentHeader";
import {
    EducationalContentContinueButton,
    EducationalContentSecondaryButton,
} from "./EducationalContentContinueButton";
import { useDispatch, useSelector } from "react-redux";
import { acceptEnrollmentHandler, acceptEnrollmentSliceAction } from "../../redux/action/guestUserAction/acceptEnrollment";
import { STATUS_CODE } from "../../common";
import { guestEnrollmentPathFromStep } from "../../common/guestEnrollmentFlow";
import Loader from "../Loder/loader";
import { useCommonMessage } from "../../common/CommonMessage";

const MAX_REASON_LENGTH = 200;

const GivingUpConfirmOption = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [reason, setReason] = useState("");
    const [validationError, setValidationError] = useState("");
    const [showConfirmOptOutModal, setShowConfirmOptOutModal] = useState(false);
    const dispatch = useDispatch();
    const acceptEnrollmentSelector = useSelector((state) => state?.acceptEnrollment);
    const messageApi = useCommonMessage();
    const location = useLocation();
    const enrollmentDecision = location.state?.enrollmentDecision;
    const isSubmitting = acceptEnrollmentSelector?.isLoading;
    const handleBack = () => {
        navigate("/guest-user/enrollment");
    };

    const handleReasonChange = (event) => {
        const rawValue = event.target.value || "";
        const nextValue = reason ? rawValue : rawValue.replace(/^\s+/, "");
        if (nextValue.length > MAX_REASON_LENGTH) {
            setReason(nextValue.slice(0, MAX_REASON_LENGTH));
            setValidationError(t("Reason cannot exceed 200 characters."));
            return;
        }
        setReason(nextValue);
        setValidationError("");
    };

    const handleReasonKeyDown = (event) => {
        if (event.key === " " && reason.length === 0) {
            event.preventDefault();
        }
    };

    const handleSubmit = () => {
        if (!reason.trim()) {
            setValidationError(t("Please enter your reason."));
            return;
        }
        if (reason.length > MAX_REASON_LENGTH) {
            setValidationError(t("Reason cannot exceed 200 characters."));
            return;
        }
        setValidationError("");
        dispatch(acceptEnrollmentHandler({
            memberAction: "NO",
            actionReason: reason.trim(),
        }));
        // navigate("/guest-user/thank-you", {
        //     state: {
        //         enrollmentDecision: "NO",
        //         declineReason: reason.trim(),
        //     },
        // });
    };

    const handleOpenConfirmOptOutModal = () => {
        if (!reason.trim() || isSubmitting) return;
        setShowConfirmOptOutModal(true);
    };

    const handleCancelConfirmOptOutModal = () => {
        setShowConfirmOptOutModal(false);
    };

    const handleConfirmOptOut = () => {
        if (isSubmitting) return;
        setShowConfirmOptOutModal(false);
        handleSubmit();
    };

    useEffect(() => {
        if (acceptEnrollmentSelector?.data?.statusCode === STATUS_CODE.OK) {
            const nextStep = acceptEnrollmentSelector?.data?.data?.nextStep;
            const nextPath = guestEnrollmentPathFromStep(nextStep);
            navigate(nextPath || "/guest-user/thank-you", {
                state: {
                    enrollmentDecision: "NO",
                },
            });
            dispatch(acceptEnrollmentSliceAction.acceptEnrollmentSliceReset());
        } else if (acceptEnrollmentSelector?.data?.error === true) {
            messageApi.error(acceptEnrollmentSelector?.data?.message || t("Unable to submit your opt-out request. Please try again."));
            dispatch(acceptEnrollmentSliceAction.acceptEnrollmentSliceReset());
        }
    }, [acceptEnrollmentSelector, dispatch, navigate, messageApi, t]);

    useEffect(() => {
        if (enrollmentDecision !== "NO") {
            navigate("/guest-user/enrollment", { replace: true });
        }
    }, [enrollmentDecision, navigate]);

    return (
        <EducationalContentHeader
            backBtnSrc={backBtn}
            backBtnMobileSrc={backBtnMobile}
            onBack={handleBack}
            title={t("Enrollment Action")}
            hideNavigation
        >
            <div className="stepperview mb-60 w-100">
                {isSubmitting && <Loader loaderTransform="loaderTransform" />}
                <div className="mb-24 belowline">
                    <h2 className="tgb fs-20 mb-12">{t("Help us understand your decision")}</h2>
                    <p className="fs-16 lh-24 colorblur" style={{ maxWidth: "380px" }}>
                        {t("Your feedback helps us improve and may help us find a better option for you.")}
                    </p>
                </div>
                <div className="">
                    <h3 className="tgb fs-20 mb-12">{t("Reason for opting out")}</h3>
                    <textarea
                        id="give-up-reason"
                        rows={5}
                        className="givingUpReasonTextarea"
                        value={reason}
                        onChange={handleReasonChange}
                        onKeyDown={handleReasonKeyDown}
                        maxLength={MAX_REASON_LENGTH}
                        placeholder={t("Type your reason here")}
                    />
                    <div className="d-flex justify-between align-center mt-8">
                        <span className="fs-14 error-message mb-0">{validationError}</span>
                        <span className="fs-14 colorblur">{`${reason.length}/${MAX_REASON_LENGTH}`}</span>
                    </div>
                </div>
            </div>

            <EducationalContentContinueButton
                hideNavigation={false}
                onContinue={handleBack}
                buttonLabel={t("Keep My Benefits")}
                disabled={isSubmitting}
            />
            <EducationalContentSecondaryButton
                hideNavigation={false}
                onContinue={handleOpenConfirmOptOutModal}
                buttonLabel={t("Confirm Opt-Out")}
                disabled={!reason.trim() || isSubmitting}
            />

            <Modal
                className="paycheckConfirmationModal"
                centered
                open={showConfirmOptOutModal}
                footer={null}
                closeIcon={null}
                maskClosable={false}
                keyboard={false}
            >
                <div className="tgb fs-20 lh-25 mb-24 text-center">
                    {t("If you continue, you will give up these benefits. Are you sure you want to proceed?")}
                </div>
                <div className="d-flex justify-center gap-10">
                    <button
                        type="button"
                        className="saveBtn cursor-pointer"
                        onClick={handleCancelConfirmOptOutModal}
                    >
                        {t("Cancel")}
                    </button>
                    <button
                        type="button"
                        className="secondaryButton contentSecondaryButton cursor-pointer"
                        onClick={handleConfirmOptOut}
                    >
                        {t("Yes")}
                    </button>
                </div>
            </Modal>
        </EducationalContentHeader>
    );
};

export default GivingUpConfirmOption;
